import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import SubcategoryList from './SubcategoryList';
import Promotion from './Promotion';

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 2rem;
`;

const ExpandingContainer = styled.div`
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth + 'px'};
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
`;

const GroupColumn = styled.nav`
  width: ${({ columns }) => Math.floor(100 / columns)}%;
  max-width: 160px;
  margin-right: 20px;

  :last-child {
    margin-right: 0;
  }
`;

const PromotionsContainer = styled.div`
  height: 100%;
  display: none;
  margin-left: 20px;

  @media (min-width: ${({ initalDisplaySize }) => initalDisplaySize + 'px'}) {
    display: flex;
  }
`;

const CategoryContent = memo(
  ({
    categoryData,
    onNodeClick,
    maxCategoryWidth,
    maxGroupColumns,
    topRowGroupsShown,
    longTopRowGroupsShown,
    bottomRowGroupsShown,
  }) => {
    let columns = categoryData.childNodes.reduce(
      (accumulator, childNode, index, array) => {
        // DISCO-1064, ignore "all" groups in the category nav.
        if (childNode.title === `All ${categoryData.title.toLowerCase()}`) {
          return accumulator;
        }

        const targetColumn = index % maxGroupColumns;

        if (!Array.isArray(accumulator[targetColumn])) {
          accumulator[targetColumn] = [];
        }

        let maxDisplayNumber = topRowGroupsShown;
        if (index >= maxGroupColumns) {
          maxDisplayNumber = bottomRowGroupsShown;
        } else if (
          index < maxGroupColumns &&
          index + maxGroupColumns > array.length - 1
        ) {
          maxDisplayNumber = longTopRowGroupsShown;
        }

        accumulator[targetColumn].push(
          <SubcategoryList
            subcategoryData={childNode}
            hasMinHeight={index < maxGroupColumns}
            maxDisplayNumber={maxDisplayNumber}
            onNodeClick={onNodeClick}
          />,
        );

        return accumulator;
      },
      [],
    );

    let numColumns = columns.length;
    let columnsMaxWidth = Math.min(maxCategoryWidth, 180 * numColumns - 20);
    let promoData = categoryData.promos || [];

    let promotions = promoData.map((data, index) => {
      const { title, source, link, path } = data;
      return (
        <Promotion
          key={index}
          index={index}
          baseWidth={columnsMaxWidth + 60}
          title={title}
          link={link}
          path={path}
          onClick={onNodeClick}
          source={source}
          promoHeight={categoryData.title === 'Flyer' ? '320px' : '230px'}
        />
      );
    });

    return (
      <ContentContainer>
        <ExpandingContainer maxWidth={columnsMaxWidth}>
          {columns.map((content, index) => (
            <GroupColumn key={index} columns={numColumns}>
              {content}
            </GroupColumn>
          ))}
        </ExpandingContainer>
        <PromotionsContainer initalDisplaySize={columnsMaxWidth + 60}>
          {promotions}
        </PromotionsContainer>
      </ContentContainer>
    );
  },
);

CategoryContent.propTypes = {
  categoryData: PropTypes.shape({
    id: PropTypes.number,
    level: PropTypes.number,
    slug: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    childNodes: PropTypes.array,
  }).isRequired,
  onNodeClick: PropTypes.func.isRequired,
};

CategoryContent.defaultProps = {
  maxCategoryWidth: 880,
  maxGroupColumns: 5,
  topRowGroupsShown: 6,
  longTopRowGroupsShown: 9,
  bottomRowGroupsShown: 4,
};

export default CategoryContent;
